/**
 * Vue magasin des tournées en cours : où en est chaque livreur, et ce qu'il lui
 * reste à faire.
 *
 * La page interroge la liste des tournées toutes les vingt secondes tant
 * qu'elle est visible. Elle annonçait « temps réel » alors qu'elle chargeait
 * une fois au montage et ne rebougeait plus : le seul mécanisme de mise à jour
 * était un bloc WebSocket commenté, écrit contre des événements que le serveur
 * n'émettait pas. La position du livreur, elle, remonte au rythme où son
 * téléphone la transmet — quelques dizaines de secondes.
 *
 * 23/10/2026 (demande de Philippe) : la carte se CADRE sur ce qu'on a choisi —
 * toutes les tournées, une tournée, un livreur qu'elle suit, ou un arrêt précis —
 * et s'y tient à chaque actualisation, tant qu'on ne la déplace pas soi-même ;
 * « Recentrer » la rend au suivi. Arrêts numérotés dans l'ordre de passage,
 * prochain arrêt annoncé, âge de la position du livreur. Sur iPhone, la carte
 * et les tournées s'empilent (elles étaient côte à côte : la carte faisait
 * quelques dizaines de pixels de large). Règles : `utils/suiviTournees.ts`.
 */
import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from 'react-leaflet';
import type L from 'leaflet';
import { Truck, Clock, Package, Navigation, CheckCircle, RefreshCw, Crosshair, ChevronDown } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import 'leaflet/dist/leaflet.css';
import { iconeArret, iconeLivreur, iconeDepot } from '../components/delivery/mapIcons';
import { logger } from '../utils/logger';
import {
  versTourneesSuivi,
  arretsRestants,
  prochainArret,
  arretsFaits,
  ageDePosition,
  cadrage as calculerCadrage,
  libelleCible,
  positionLivreur,
  positionArret,
  COULEURS_TOURNEES,
  type TourneeSuivi,
  type ArretSuivi,
  type Cible,
  type Cadrage,
} from '../utils/suiviTournees';

/** Rafraîchissement de la liste tant que la page est sous les yeux de quelqu'un. */
const INTERVALLE_MS = 20_000;

const heure = (iso: string | null) =>
  iso
    ? new Date(iso)
        .toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' })
        .replace(':', ' h ')
    : null;

const ETAT_LIBELLE: Record<ArretSuivi['etat'], string> = {
  a_faire: 'À livrer',
  en_route: 'En route',
  livre: 'Livré',
  echec: 'Non livré',
};

/**
 * Pilote la carte : cadrage sur la cible tant que le suivi est actif, arrêt du
 * suivi dès que la main de l'utilisateur bouge la carte, et taille recalculée
 * quand le conteneur change (rotation de l'iPhone, barre d'adresse, panneau).
 */
function PiloteCarte({
  cadrage,
  suivre,
  demande,
  onMainLibre,
}: {
  cadrage: Cadrage | null;
  suivre: boolean;
  demande: number;
  onMainLibre: () => void;
}) {
  const map = useMap();
  // Levé pendant NOS déplacements : un zoom qu'on lance n'est pas un geste de la main.
  const parNous = useRef(false);
  // Numéro du dernier déplacement lancé : la fin d'un déplacement plus ancien ne
  // doit jamais baisser le drapeau d'un plus récent.
  const generation = useRef(0);
  const enAttente = useRef(false);
  const etat = useRef({ cadrage, suivre });
  etat.current = { cadrage, suivre };

  const appliquer = useCallback(() => {
    const { cadrage: c, suivre: s } = etat.current;
    if (!s || !c) return;
    // Pendant un zoom animé, Leaflet IGNORE tout nouveau cadrage sans rien dire :
    // « Suivre Paul » touché pendant le cadrage d'ouverture laissait la carte au
    // zoom 11. On le rejoue à la fin du zoom (23/10/2026).
    if ((map as unknown as { _animatingZoom?: boolean })._animatingZoom) {
      if (!enAttente.current) {
        enAttente.current = true;
        map.once('zoomend', () => {
          enAttente.current = false;
          appliquer();
        });
      }
      return;
    }
    // Un glissement en cours (la fiche d'un arrêt qui se place) se termine ICI,
    // avant le drapeau. Sinon `setView` l'arrête lui-même, son « moveend » faisait
    // tomber le drapeau, et notre propre zoom passait pour la main de l'utilisateur :
    // le suivi se coupait juste après avoir choisi un arrêt (banc iPhone, 23/10/2026).
    map.stop();
    const n = ++generation.current;
    parNous.current = true;
    const fin = () => {
      if (generation.current === n) parNous.current = false;
    };
    if (c.mode === 'cadre') {
      map.fitBounds(c.points, { padding: [40, 40], maxZoom: 16 });
    } else {
      map.setView(c.point, c.zoom);
    }
    // Écouté APRÈS : un déplacement sans animation est déjà terminé à cette ligne.
    map.once('moveend', fin);
    // Vue inchangée, ou fin déjà passée : le drapeau tombe quand même.
    window.setTimeout(fin, 800);
  }, [map]);

  useMapEvents({
    dragstart: () => onMainLibre(),
    zoomstart: () => {
      if (!parNous.current) onMainLibre();
    },
  });

  // La place de la carte change (liste des tournées qui arrive à côté, rotation
  // de l'iPhone, barre d'adresse) : Leaflet doit le savoir, ET le cadre d'avant
  // ne tient plus — sur ordinateur, livreur et arrêts sortaient par la droite.
  useEffect(() => {
    const el = map.getContainer();
    let taille = `${el.clientWidth}x${el.clientHeight}`;
    const ro = new ResizeObserver(() => {
      const t = `${el.clientWidth}x${el.clientHeight}`;
      if (t === taille) return;
      taille = t;
      map.invalidateSize();
      appliquer();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [map, appliquer]);

  const cle = JSON.stringify(cadrage);
  useEffect(() => {
    appliquer();
  }, [cle, suivre, demande, appliquer]);

  return null;
}

export default function DeliveryDashboard() {
  const { tenant } = useTenant();
  const [tournees, setTournees] = useState<TourneeSuivi[]>([]);
  const [depot, setDepot] = useState<[number, number] | null>(null);
  const [chargeA, setChargeA] = useState<Date | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [cible, setCible] = useState<Cible>({ type: 'tout' });
  const [suivre, setSuivre] = useState(true);
  const [demande, setDemande] = useState(0);
  const [ouvertes, setOuvertes] = useState<Set<string>>(new Set());
  const [maintenant, setMaintenant] = useState(() => new Date());
  const carteRef = useRef<HTMLDivElement>(null);
  const marqueursArrets = useRef(new Map<string, L.Marker>());
  const marqueursLivreurs = useRef(new Map<string, L.Marker>());

  const entetes = useCallback(
    () => ({
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      'X-Tenant-ID': tenant?.id || '',
    }),
    [tenant],
  );

  const loadActiveDeliveries = useCallback(async () => {
    if (!tenant) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/customer-deliveries?tenantId=${tenant.id}`, {
        headers: entetes(),
      });
      if (!response.ok) {
        logger.error('[DeliveryDashboard] API response not OK:', response.status, response.statusText);
        throw new Error('Failed to load deliveries');
      }
      const data = await response.json();
      const enRoute = versTourneesSuivi(data);
      // Une seule ligne : cette fonction tourne toutes les 20 s.
      logger.log(`[DeliveryDashboard] ${enRoute.length}/${Array.isArray(data) ? data.length : 0} tournée(s) en route`);
      setTournees(enRoute);
      setChargeA(new Date());
      setErreur(null);
    } catch (error) {
      logger.error('[DeliveryDashboard] Error loading deliveries:', error);
      setErreur('Les tournées n’ont pas pu être relues — nouvel essai dans 20 secondes.');
    }
  }, [tenant, entetes]);

  // Le point de départ des tournées : la carte s'y pose quand rien ne roule.
  useEffect(() => {
    if (!tenant) return;
    fetch(`${import.meta.env.VITE_API_URL}/customer-deliveries/settings`, { headers: entetes() })
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => {
        const lat = Number(s?.depot?.lat);
        const lng = Number(s?.depot?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) setDepot([lat, lng]);
      })
      .catch(() => undefined);
  }, [tenant, entetes]);

  // Une page laissée ouverte sur un écran de bureau ne doit pas interroger le
  // serveur toute la nuit : le rafraîchissement s'arrête avec l'onglet, et
  // reprend par une lecture immédiate puisque ce qui est affiché a vieilli.
  useEffect(() => {
    if (!tenant) return;
    const tour = () => {
      if (document.visibilityState === 'hidden') return;
      void loadActiveDeliveries();
    };
    tour();
    const timer = window.setInterval(tour, INTERVALLE_MS);
    const surVisibilite = () => {
      if (document.visibilityState === 'visible') tour();
    };
    document.addEventListener('visibilitychange', surVisibilite);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', surVisibilite);
    };
  }, [tenant, loadActiveDeliveries]);

  // L'âge des positions avance entre deux lectures.
  useEffect(() => {
    const t = window.setInterval(() => setMaintenant(new Date()), 15_000);
    return () => window.clearInterval(t);
  }, []);

  /** Choisir où regarder : la carte s'y cale et s'y tient. */
  const viser = (c: Cible) => {
    setCible(c);
    setSuivre(true);
    setDemande((n) => n + 1);
    // Sur téléphone la carte est AU-DESSUS des tournées : sans ça, on choisit un
    // arrêt dans la liste et on ne voit pas la carte bouger (vu au banc iPhone).
    const el = carteRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.top < 64 || r.bottom > window.innerHeight) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Un arrêt visé ouvre sa fiche une fois la carte arrivée dessus ; un livreur aussi.
  useEffect(() => {
    if (demande === 0) return;
    const t = window.setTimeout(() => {
      if (cible.type === 'arret') marqueursArrets.current.get(cible.arretId)?.openPopup();
      if (cible.type === 'livreur') marqueursLivreurs.current.get(cible.id)?.openPopup();
    }, 450);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demande]);

  const cadrage = calculerCadrage(tournees, cible, depot);
  const couleurDe = (id: string) => COULEURS_TOURNEES[Math.max(0, tournees.findIndex((t) => t.id === id)) % COULEURS_TOURNEES.length];

  const totalArrets = tournees.reduce((s, t) => s + t.arrets.length, 0);
  const livres = tournees.reduce((s, t) => s + arretsFaits(t), 0);
  const restants = tournees.reduce((s, t) => s + arretsRestants(t).length, 0);
  const centreInitial: [number, number] = depot ?? [50.85, 4.35];

  const basculer = (id: string) =>
    setOuvertes((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  return (
    <div className="flex flex-col gap-3 lg:h-[calc(100dvh-8rem)]">
      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Navigation className="w-6 h-6 text-blue-600 shrink-0" />
            Tournées en cours
          </h1>
          {/* Annoncer la cadence évite de croire l'écran figé, ou à jour à
              la seconde : ni l'un ni l'autre n'est vrai. */}
          <p className="text-xs sm:text-sm text-gray-500">
            Actualisé toutes les 20 secondes{chargeA ? ` · lu à ${heure(chargeA.toISOString())}` : ''}
          </p>
        </div>
        <button
          onClick={() => loadActiveDeliveries()}
          className="flex items-center gap-2 px-3 sm:px-4 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          aria-label="Actualiser"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Actualiser</span>
        </button>
      </div>

      {erreur && <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">{erreur}</div>}

      {/* Compteurs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { icone: Truck, libelle: 'Tournées en route', valeur: tournees.length, ton: 'bg-blue-50 text-blue-900' },
          { icone: Package, libelle: 'Arrêts', valeur: totalArrets, ton: 'bg-purple-50 text-purple-900' },
          { icone: CheckCircle, libelle: 'Livrés', valeur: livres, ton: 'bg-green-50 text-green-900' },
          { icone: Clock, libelle: 'Reste à livrer', valeur: restants, ton: 'bg-orange-50 text-orange-900' },
        ].map(({ icone: Icone, libelle, valeur, ton }) => (
          <div key={libelle} className={`rounded-lg px-3 py-2 ${ton}`}>
            <div className="flex items-center gap-1.5 text-xs sm:text-sm font-medium opacity-80">
              <Icone className="w-4 h-4 shrink-0" />
              <span className="truncate">{libelle}</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold tabular-nums">{valeur}</div>
          </div>
        ))}
      </div>

      {/* Sur quoi caler la carte */}
      {tournees.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => viser({ type: 'tout' })}
            className={`shrink-0 px-3 min-h-[40px] rounded-full border text-sm font-medium ${
              cible.type === 'tout' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Toutes les tournées
          </button>
          {tournees.map((t) => {
            const active = (cible.type === 'tournee' || cible.type === 'livreur') && cible.id === t.id;
            return (
              <button
                key={t.id}
                onClick={() => viser({ type: 'tournee', id: t.id })}
                className={`shrink-0 inline-flex items-center gap-2 px-3 min-h-[40px] rounded-full border text-sm font-medium ${
                  active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'
                }`}
              >
                <span className="w-3 h-3 rounded-full" style={{ background: couleurDe(t.id) }} />
                {t.livreur}
                <span className="tabular-nums opacity-70">
                  {arretsFaits(t)}/{t.arrets.length}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Carte + tournées : empilées sur téléphone, côte à côte sur ordinateur */}
      <div className="flex flex-col lg:flex-row gap-3 lg:flex-1 lg:min-h-0">
        {/* `isolate` : les calques de Leaflet montent à z-index 1000 et passaient
            DEVANT le menu latéral (z-20) ouvert sur iPhone (capture du 23/10/2026).
            Le contexte d'empilement les garde à l'intérieur de la carte. */}
        <div
          ref={carteRef}
          className="relative isolate scroll-mt-20 h-[58svh] min-h-[320px] lg:h-auto lg:flex-1 rounded-xl overflow-hidden border border-gray-200 bg-gray-100"
        >
          <MapContainer center={centreInitial} zoom={12} style={{ height: '100%', width: '100%' }}>
            <PiloteCarte cadrage={cadrage} suivre={suivre} demande={demande} onMainLibre={() => setSuivre(false)} />
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {depot && (
              <Marker position={depot} icon={iconeDepot}>
                <Popup>Magasin — départ des tournées</Popup>
              </Marker>
            )}

            {tournees.map((t) => {
              const couleur = couleurDe(t.id);
              const livreur = positionLivreur(t);
              const trajet: Array<[number, number]> = [
                ...(livreur ? [livreur] : []),
                ...arretsRestants(t)
                  .map(positionArret)
                  .filter((p): p is [number, number] => p !== null),
              ];
              const prochain = prochainArret(t);
              const age = ageDePosition(t.derniereMaj, maintenant);
              return (
                <Fragment key={t.id}>
                  {trajet.length > 1 && (
                    <Polyline positions={trajet} pathOptions={{ color: couleur, weight: 3, opacity: 0.7, dashArray: '8 8' }} />
                  )}
                  {t.arrets.map((a) => {
                    const p = positionArret(a);
                    if (!p) return null;
                    return (
                      <Marker
                        key={a.id}
                        position={p}
                        icon={iconeArret(a.rang, couleur, a.etat, prochain?.id === a.id)}
                        zIndexOffset={prochain?.id === a.id ? 500 : a.etat === 'livre' ? -100 : 0}
                        ref={(m) => {
                          if (m) marqueursArrets.current.set(a.id, m);
                        }}
                        eventHandlers={{ click: () => viser({ type: 'arret', tourneeId: t.id, arretId: a.id }) }}
                      >
                        <Popup>
                          <div className="text-sm space-y-0.5">
                            <div className="font-bold">
                              {a.rang}. {a.client}
                            </div>
                            <div className="text-gray-600">{a.adresse}</div>
                            <div className="text-gray-500 text-xs">
                              {a.orderNumber} · {t.livreur}
                            </div>
                            <div className="text-xs font-medium">
                              {ETAT_LIBELLE[a.etat]}
                              {a.etat === 'livre' && a.fait ? ` à ${heure(a.fait)}` : ''}
                              {(a.etat === 'a_faire' || a.etat === 'en_route') && a.prevu ? ` · prévu vers ${heure(a.prevu)}` : ''}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                  {livreur && (
                    <Marker
                      position={livreur}
                      icon={iconeLivreur(t.livreur, couleur, !!age?.ancienne)}
                      zIndexOffset={1000}
                      ref={(m) => {
                        if (m) marqueursLivreurs.current.set(t.id, m);
                      }}
                      eventHandlers={{ click: () => viser({ type: 'livreur', id: t.id }) }}
                    >
                      <Popup>
                        <div className="text-sm space-y-0.5">
                          <div className="font-bold">{t.livreur}</div>
                          <div>
                            {arretsFaits(t)}/{t.arrets.length} livrés
                          </div>
                          {age && <div className={age.ancienne ? 'text-amber-700' : 'text-gray-600'}>Position {age.texte}</div>}
                          {prochain && (
                            <div className="text-gray-600">
                              Prochain : {prochain.rang}. {prochain.client}
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </Fragment>
              );
            })}
          </MapContainer>

          {/* Suivi actif ou repris en main : on le voit, et on y revient d'un geste.
              EN BAS de la carte : en haut, il cachait le titre de la fiche d'un
              arrêt (les fiches s'ouvrent au-dessus des marqueurs) ; en bas, il est
              aussi sous le pouce sur un iPhone. */}
          {tournees.length > 0 && (
            <div className="absolute bottom-7 inset-x-0 z-[1000] flex justify-center px-3 pointer-events-none">
              {suivre ? (
                <span className="max-w-full truncate px-3 py-1.5 rounded-full bg-white/95 border border-gray-200 text-xs font-medium text-gray-700 shadow">
                  Suivi : {libelleCible(tournees, cible)}
                </span>
              ) : (
                <button
                  onClick={() => viser(cible)}
                  className="pointer-events-auto max-w-full inline-flex items-center gap-2 px-4 min-h-[44px] rounded-full bg-blue-600 text-white text-sm font-semibold shadow-lg"
                >
                  <Crosshair className="w-4 h-4 shrink-0" />
                  <span className="truncate">Recentrer sur {libelleCible(tournees, cible)}</span>
                </button>
              )}
            </div>
          )}

          {tournees.length === 0 && (
            <div className="absolute inset-x-3 bottom-3 z-[1000] rounded-xl bg-white/95 border border-gray-200 p-3 text-sm text-gray-700 shadow">
              Aucune tournée en route pour le moment. Les tournées du jour se préparent dans{' '}
              <Link to="/customer-deliveries" className="font-semibold text-blue-700 underline">
                Livraisons Clients
              </Link>
              .
            </div>
          )}
        </div>

        {/* Les tournées */}
        {tournees.length > 0 && (
          <div className="lg:w-96 lg:overflow-y-auto space-y-3">
            {tournees.map((t) => {
              const couleur = couleurDe(t.id);
              const age = ageDePosition(t.derniereMaj, maintenant);
              const prochain = prochainArret(t);
              const faits = arretsFaits(t);
              const pourcentage = t.arrets.length ? Math.round((faits / t.arrets.length) * 100) : 0;
              const ouverte = ouvertes.has(t.id);
              return (
                <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-3" style={{ borderLeft: `6px solid ${couleur}` }}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-gray-900 text-lg leading-tight">{t.livreur}</h3>
                    <span
                      className={`shrink-0 px-2 py-1 rounded-full text-xs font-medium ${
                        t.statut === 'STARTED' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {t.statut === 'STARTED' ? 'Démarrée' : 'En route'}
                    </span>
                  </div>

                  <div className="mt-1 text-sm text-gray-600 flex flex-wrap gap-x-3">
                    <span className="tabular-nums">
                      {faits}/{t.arrets.length} livrés
                    </span>
                    {age ? (
                      <span className={age.ancienne ? 'text-amber-700 font-medium' : ''}>
                        Position {age.texte}
                        {age.ancienne ? ' — ancienne' : ''}
                      </span>
                    ) : (
                      <span className="text-amber-700">Position pas encore reçue</span>
                    )}
                  </div>

                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${pourcentage}%`, background: couleur }} />
                  </div>

                  {prochain ? (
                    <button
                      onClick={() => viser({ type: 'arret', tourneeId: t.id, arretId: prochain.id })}
                      className="mt-3 w-full text-left rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 hover:border-blue-300"
                    >
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Prochain arrêt</div>
                      <div className="font-medium text-gray-900">
                        {prochain.rang}. {prochain.client}
                      </div>
                      <div className="text-sm text-gray-600">{prochain.adresse}</div>
                      {prochain.prevu && <div className="text-xs text-gray-500">Prévu vers {heure(prochain.prevu)}</div>}
                    </button>
                  ) : (
                    <div className="mt-3 text-sm text-green-700 font-medium">Tous les arrêts sont faits.</div>
                  )}

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => viser({ type: 'livreur', id: t.id })}
                      className="min-h-[44px] rounded-lg border border-gray-300 text-sm font-medium text-gray-800 hover:bg-gray-50"
                    >
                      📍 Suivre {t.livreur.split(/\s+/)[0]}
                    </button>
                    <button
                      onClick={() => viser({ type: 'tournee', id: t.id })}
                      className="min-h-[44px] rounded-lg border border-gray-300 text-sm font-medium text-gray-800 hover:bg-gray-50"
                    >
                      🗺️ Toute la tournée
                    </button>
                  </div>

                  <button
                    onClick={() => basculer(t.id)}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1 min-h-[40px] text-sm text-gray-600"
                    aria-expanded={ouverte}
                  >
                    {ouverte ? 'Masquer les arrêts' : `Voir les ${t.arrets.length} arrêts`}
                    <ChevronDown className={`w-4 h-4 transition-transform ${ouverte ? 'rotate-180' : ''}`} />
                  </button>

                  {ouverte && (
                    <ol className="mt-1 divide-y divide-gray-100">
                      {t.arrets.map((a) => (
                        <li key={a.id}>
                          <button
                            onClick={() => viser({ type: 'arret', tourneeId: t.id, arretId: a.id })}
                            className="w-full text-left flex items-start gap-3 py-2 min-h-[44px] hover:bg-gray-50 rounded"
                          >
                            <span
                              className="mt-0.5 shrink-0 w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center"
                              style={{
                                background: a.etat === 'livre' ? '#9ca3af' : a.etat === 'echec' ? '#dc2626' : couleur,
                              }}
                            >
                              {a.etat === 'livre' ? '✓' : a.etat === 'echec' ? '✗' : a.rang}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className={`block font-medium ${a.etat === 'livre' ? 'text-gray-500' : 'text-gray-900'}`}>
                                {a.client}
                              </span>
                              <span className="block text-xs text-gray-500 truncate">{a.adresse || 'Adresse inconnue'}</span>
                              <span className="block text-xs text-gray-500">
                                {ETAT_LIBELLE[a.etat]}
                                {a.etat === 'livre' && a.fait ? ` à ${heure(a.fait)}` : ''}
                                {(a.etat === 'a_faire' || a.etat === 'en_route') && a.prevu ? ` · prévu vers ${heure(a.prevu)}` : ''}
                                {a.lat === null ? ' · pas sur la carte' : ''}
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
